import { useSignal } from "@preact/signals";
import { USER_AGENTS } from "../utils/user-agents.ts";

interface ArticleFormProps {
  readonly initialUrl?: string;
}

function detectsMediumUrl(urlStr: string): boolean {
  try {
    const h = new URL(urlStr).hostname;
    return h === "medium.com" || h.endsWith(".medium.com");
  } catch {
    return false;
  }
}

export default function ArticleForm({ initialUrl }: ArticleFormProps) {
  const url = useSignal(initialUrl || "");
  const userAgent = useSignal<keyof typeof USER_AGENTS>("googleBot");
  const bypassPaywall = useSignal(false);

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (url.value.trim()) {
      const params = new URLSearchParams({
        url: url.value.trim(),
        userAgent: userAgent.value,
      });
      if (bypassPaywall.value) params.set("bypassPaywall", "true");
      globalThis.location.href = `/article?${params.toString()}`;
    }
  };

  const isMedium = detectsMediumUrl(url.value);

  return (
    <form onSubmit={handleSubmit} class="space-y-4">
      <div>
        <label htmlFor="url" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Article URL
        </label>
        <input
          type="url"
          id="url"
          value={url.value}
          onInput={(e) => url.value = (e.target as HTMLInputElement).value}
          placeholder="https://example.com/article"
          required
          class="w-full px-3 py-2 rounded-md shadow-sm border bg-white border-gray-300 text-slate-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 transition-colors duration-200"
        />
      </div>

      <div>
        <label htmlFor="userAgent" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          User Agent
        </label>
        <select
          id="userAgent"
          value={userAgent.value}
          onChange={(e) => userAgent.value = (e.target as HTMLSelectElement).value as keyof typeof USER_AGENTS}
          class="w-full px-3 py-2 rounded-md shadow-sm border bg-white border-gray-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-200"
        >
          <option value="googleBot">Google Bot (Default)</option>
          <option value="chrome">Chrome</option>
          <option value="firefox">Firefox</option>
          <option value="safari">Safari</option>
          <option value="edge">Edge</option>
        </select>
      </div>

      <div class={`flex items-start gap-3 rounded-md px-3 py-2.5 border transition-colors duration-200 ${bypassPaywall.value ? "bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700" : "bg-gray-50 border-gray-200 dark:bg-gray-700/40 dark:border-gray-600"}`}>
        <input
          type="checkbox"
          id="bypassPaywall"
          checked={bypassPaywall.value}
          onChange={(e) => bypassPaywall.value = (e.target as HTMLInputElement).checked}
          class="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
        />
        <div class="flex-1 min-w-0">
          <label htmlFor="bypassPaywall" class="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            Bypass Medium paywall
          </label>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {isMedium
              ? "Medium URL detected — uses the mobile GraphQL API to fetch the full article."
              : "For Medium articles and custom Medium domains (e.g. towardsdatascience.com)."}
          </p>
        </div>
        {isMedium && (
          <span class="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            Medium
          </span>
        )}
      </div>

      <button
        type="submit"
        class="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors flex items-center justify-center gap-2"
      >
        <i class="fas fa-eye"></i>
        <span class="hidden md:inline">View Article</span>
      </button>
    </form>
  );
}
